import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useRazorpay from '../../hooks/useRazorpay';
import Swal from 'sweetalert2';
import { saveDiagnosisReport, saveDraftReport } from '../../services/diagnosisReports.service';
import { recordReferral } from '../../services/franchise.service';
import Step1PersonalInfo from './Step1PersonalInfo';
import Step1IncomeDetails from './Step1IncomeDetails';
import BusinessIncomeForm from './BusinessIncomeForm';
import BusinessExpensesForm from './BusinessExpensesForm';
import BusinessInvestmentForm from './BusinessInvestmentForm';
import SalaryIncomeForm from './SalaryIncomeForm';
import SalaryExpensesForm from './SalaryExpensesForm';
import SalaryInvestmentForm from './SalaryInvestmentForm';
import Step2Salary from './Step2Salary';
import Step2TaxDetails from './Step2TaxDetails';
import Step3Expenses from './Step3Expenses';
import Step4Review from './Step4Review';
import Step5Summary from './Step5Summary';
import Step6LandGold from './Step6LandGold';
import PartnershipTaxForm from './PartnershipTaxForm';
import JointFinancialForm from './JointFinancialForm';
import { useAuth } from '../../hooks/useAuth';
import './FinancialDiagnosisForm.css';

const FinancialDiagnosisForm = ({ isAdminDemo = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const refCodeFromUrl = queryParams.get('ref');
    
    const { user } = useAuth();
    const { initiatePayment } = useRazorpay();
    const [currentStep, setCurrentStep] = useState(1);
    const [isProcessing, setIsProcessing] = useState(false);
    const [reportId] = useState(() => {
        const pendingData = localStorage.getItem('pendingDiagnosisData');
        if (pendingData) {
            try {
                const parsed = JSON.parse(pendingData);
                if (parsed.reportId) return parsed.reportId;
                if (parsed.id) return parsed.id;
            } catch (e) {
                console.error('Failed to parse pending reportId:', e);
            }
        }
        return `RPT${Date.now()}`;
    });

    const [formData, setFormData] = useState({
        personalInfo: {
            name: user?.name || '',
            email: user?.email || '', // Pre-fill from auth
            dateOfBirth: '',
            age: '',
            gender: '',
            incomeType: '', // Added income type (salary/business)
            familyType: '', // Added family type (single/couple)
            monthlySalary: '',
            otherIncomeType: '', // Added other income type
            otherIncomeAmount: '', // Added other income amount
            cityType: '',
            rent: '',
            homeLoan: '',
            phone: '',
            education: '',
            address: '',
            franchiseReferralCode: user?.franchiseReferralCode || refCodeFromUrl || '',
            referredBy: user?.referredBy || '',
            referredByName: user?.referredByName || ''
        },
        businessDetails: {},
        businessExpenses: {},
        businessInvestment: {},
        salaryIncome: {},
        salaryExpenses: {},
        salaryInvestment: {},
        jointSalary: {}, // Added for couple's joint form
        landGold: {},
        salary: {
            monthlySalary: '',
            businessIncome: '',
            farmIncome: '',
            capitalGain: '',
            fdInterest: '',
            totalIncome: 0
        },
        expenses: {
            rentEMI: '',
            grocery: '',
            bills: '',
            transport: '',
            totalLoanEMIs: '',
            homeLoan: '',
            carLoan: '',
            personalLoan: '',
            insurance: '',
            education: '',
            dining: '',
            shopping: '',
            monthlyInvestment: '',
            emergencyFund: '',
            totalExpenses: 0,
            surplus: 0
        },
        taxDetails: {
            epf: '',
            lifeHealthInsurance: '',
            investments80C: '',
            homeLoanPrincipal: '',
            others: ''
        }
    });

    useEffect(() => {
        const pendingData = localStorage.getItem('pendingDiagnosisData');
        if (pendingData) {
            try {
                const parsed = JSON.parse(pendingData);
                // Pre-fill user details if logged in
                if (user) {
                    parsed.personalInfo = {
                        ...parsed.personalInfo,
                        name: parsed.personalInfo.name || user.name || '',
                        email: parsed.personalInfo.email || user.email || '',
                    };
                }
                setFormData(parsed);
                setCurrentStep(6);
                localStorage.removeItem('pendingDiagnosisData');
            } catch (e) {
                console.error('Failed to restore pending diagnosis data:', e);
            }
        }
    }, [user]);

    useEffect(() => {
        // Don't save draft in admin demo mode
        if (isAdminDemo) {
            return;
        }

        // Don't save draft if it's the initial render of step 1 and nothing is filled yet
        if (currentStep === 1 && !formData.personalInfo.name && !formData.personalInfo.email) {
            return;
        }
        
        // Save draft report to Firestore
        saveDraftReport(reportId, formData, user?.id || user?.uid);
    }, [currentStep, reportId, user, isAdminDemo]);

    const totalSteps = 6; // Now 6 steps: 1-Personal, 2-Income, 3-Expenses, 4-Investments, 5-LandGold, 6-Summary

    const updateFormData = (section, data) => {
        setFormData(prev => ({
            ...prev,
            [section]: data
        }));
    };

    const nextStep = (finalStepData = null) => {
        let updatedData = { ...formData };
        if (finalStepData) {
            if (formData.personalInfo.familyType === 'couple' && currentStep === 2) {
                updatedData.jointSalary = finalStepData;
            }
        }

        // If couple, Step 2 is the joint form, then jump to Summary (Step 6)
        if (formData.personalInfo.familyType === 'couple' && currentStep === 2) {
            setFormData(updatedData);
            setCurrentStep(6);
            if (!isAdminDemo && reportId) {
                saveDraftReport(reportId, updatedData, user?.id || user?.uid);
            }
            window.scrollTo(0, 0);
            return;
        }

        if (currentStep >= 6) {
            handlePayment(finalStepData);
            return;
        }
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
    };

    const prevStep = () => {
        // If couple and on Summary, go back to Joint Form (Step 2)
        if (formData.personalInfo.familyType === 'couple' && currentStep === 6) {
            setCurrentStep(2);
            window.scrollTo(0, 0);
            return;
        }

        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
        window.scrollTo(0, 0);
    };

    const calculateExpenses = (dataToUse = formData) => {
        let aggregatedExpenses = {};

        if (dataToUse.personalInfo.familyType === 'couple') {
            // For couples, expenses are in jointSalary.sectionC
            const c = dataToUse.jointSalary?.sectionC || {};
            return {
                totalExpenses: Object.values(c).reduce((sum, val) => sum + (parseFloat(val) || 0), 0),
                ...c
            };
        }

        if (dataToUse.personalInfo.incomeType === 'business_partnership') {
            // Partnership details already include a calculated totalExpenses
            return {
                totalExpenses: parseFloat(dataToUse.businessDetails.totalExpenses) || 0,
                surplus: (parseFloat(dataToUse.businessDetails.totalRevenue) || 0) - (parseFloat(dataToUse.businessDetails.totalExpenses) || 0)
            };
        }

        if (dataToUse.personalInfo.incomeType === 'business') {
            // Business Users (Proprietorship/Manufacturing)
            // Combine expenses from businessDetails (Step 2) and businessExpenses (Step 3)
            const operatingExpenses = { ...dataToUse.businessDetails };

            // Remove income/turnover/structural fields from operating expenses to get pure expenses
            const nonExpenseFields = [
                'businessType', 'grossTurnover', 'totalProfit', 'partnershipTurnover', 'taxableProfit',
                'totalAnnualRevenue', 'totalExpenses', 'remunerationToPartners', 'interestOnCapital',
                'numberOfPartners', 'profitSharingRatio', 'netProfit', 'taxableIncome', 'taxAmount', 'cessAmount', 'totalTaxPayable',
                'otherIncomeAmount', 'savingInterest', 'fdInterest', 'rdInterest', 'licMaturity', 'agriIncome'
            ];

            nonExpenseFields.forEach(field => delete operatingExpenses[field]);

            aggregatedExpenses = {
                ...operatingExpenses,
                ...dataToUse.businessExpenses
            };
        } else {
            // Salary Users
            // 1. Fixed Expenses from SalaryIncomeForm
            const fixedExpenses = {
                rentEMI: dataToUse.salaryIncome?.rentEMI,
                societyMaintenance: dataToUse.salaryIncome?.societyMaintenance,
                propertyTax: dataToUse.salaryIncome?.propertyTax,
                homeLoanEMI: dataToUse.salaryIncome?.homeLoanEMI,
                carLoanEMI: dataToUse.salaryIncome?.carLoanEMI,
                personalLoanEMI: dataToUse.salaryIncome?.personalLoanEMI,
                educationLoanEMI: dataToUse.salaryIncome?.educationLoanEMI,
                creditCardEMI: dataToUse.salaryIncome?.creditCardEMI,
                otherLoanEMI: dataToUse.salaryIncome?.otherLoanEMI,
                lifeInsurance: dataToUse.salaryIncome?.lifeInsurance,
                healthInsurance: dataToUse.salaryIncome?.healthInsurance,
                criticalIllness: dataToUse.salaryIncome?.criticalIllness,
                motorInsurance: dataToUse.salaryIncome?.motorInsurance,
                homeInsurance: dataToUse.salaryIncome?.homeInsurance,
                epf: dataToUse.salaryIncome?.epf,
                vpf: dataToUse.salaryIncome?.vpf,
                ppf: dataToUse.salaryIncome?.ppf,
                nps_80ccd2: dataToUse.salaryIncome?.nps_80ccd2,
                sukanyaSamriddhi: dataToUse.salaryIncome?.sukanyaSamriddhi,
                electricity: dataToUse.salaryIncome?.electricity,
                water: dataToUse.salaryIncome?.water,
                gas: dataToUse.salaryIncome?.gas,
                internet: dataToUse.salaryIncome?.internet,
                broadband: dataToUse.salaryIncome?.broadband,
                ottSubscriptions: dataToUse.salaryIncome?.ottSubscriptions,
                cable: dataToUse.salaryIncome?.cable
            };

            // 2. Living Expenses from SalaryExpensesForm
            const livingExpenses = { ...dataToUse.salaryExpenses };

            // 3. Discretionary & Lifestyle from SalaryInvestmentForm
            // Filter out actual investments/savings to avoid double counting if user considers them savings involved in "Total Expenses" calculation
            const {
                elss, mutualFunds, stocksEquity, recurringDeposits, fixedDeposits, goldBonds, realEstate,
                liquidFund, emergencyFund,
                ...otherInvestments
            } = dataToUse.salaryInvestment || {};

            aggregatedExpenses = {
                ...fixedExpenses,
                ...livingExpenses,
                ...otherInvestments
            };
        }

        // Clean up and Calculate Total
        const cleanExpenses = {};
        let totalVal = 0;

        Object.entries(aggregatedExpenses).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                const numVal = parseFloat(value) || 0;
                cleanExpenses[key] = numVal; // Store as number
                if (!['totalExpenses', 'surplus'].includes(key)) {
                    totalVal += numVal;
                }
            }
        });

        cleanExpenses.totalExpenses = totalVal;
        return cleanExpenses;
    };

    const calculateBusinessTax = (netProfit) => {
        // Simplified Tax Calculation based on 2024-25 Individual/HUF Slabs (Old Regime & New Regime Mix or Standard assumption)
        // For Proprietor, tax is as per individual slabs.
        // Let's use New Tax Regime (default) for calculation as it's common now.
        // 0-3L: Nil, 3-6L: 5%, 6-9L: 10%, 9-12L: 15%, 12-15L: 20%, >15L: 30%

        let taxableIncome = Math.max(0, netProfit);
        let tax = 0;

        if (taxableIncome > 300000) {
            if (taxableIncome <= 600000) {
                tax += (taxableIncome - 300000) * 0.05;
            } else {
                tax += 15000; // 5% on 3L
                if (taxableIncome <= 900000) {
                    tax += (taxableIncome - 600000) * 0.10;
                } else {
                    tax += 30000; // 10% on 3L
                    if (taxableIncome <= 1200000) {
                        tax += (taxableIncome - 900000) * 0.15;
                    } else {
                        tax += 45000; // 15% on 3L
                        if (taxableIncome <= 1500000) {
                            tax += (taxableIncome - 1200000) * 0.20;
                        } else {
                            tax += 60000; // 20% on 3L
                            tax += (taxableIncome - 1500000) * 0.30;
                        }
                    }
                }
            }
        }

        // Surcharge
        let surcharge = 0;
        if (taxableIncome > 5000000 && taxableIncome <= 10000000) surcharge = tax * 0.10;
        else if (taxableIncome > 10000000 && taxableIncome <= 20000000) surcharge = tax * 0.15;
        else if (taxableIncome > 20000000 && taxableIncome <= 50000000) surcharge = tax * 0.25;
        else if (taxableIncome > 50000000) surcharge = tax * 0.25; // Capped at 25% for New Regime

        const totalTaxBeforeCess = tax + surcharge;
        const cess = totalTaxBeforeCess * 0.04;
        const totalTaxPayable = totalTaxBeforeCess + cess;

        return {
            tax: Math.round(tax),
            surcharge: Math.round(surcharge),
            cess: Math.round(cess),
            totalTaxPayable: Math.round(totalTaxPayable)
        };
    };

    const handlePayment = async (finalStepData = null) => {
        // Create a fresh copy of everything including the very latest data passed from the final form step
        const currentData = { ...formData };
        
        if (finalStepData) {
            // Determine which section to update based on income type and current step
            if (currentStep === 2 && formData.personalInfo.incomeType === 'business_partnership') {
                currentData.businessDetails = { ...formData.businessDetails, ...finalStepData };
            } else if (currentStep === 4 || currentStep === 5) {
                // Step 4 for business (Investments), step 5 for salary (Summary - no new data)
                if (formData.personalInfo.incomeType === 'business' || formData.personalInfo.incomeType === 'business_partnership') {
                    currentData.businessInvestment = { ...formData.businessInvestment, ...finalStepData };
                }
                // For salary on step 5, no new data to add (summary only shows existing data)
            }
        }

        // Check if user is logged in before proceeding to payment
        if (!user && !isAdminDemo) {
            // Save state to localStorage, including reportId
            const dataToSave = {
                ...currentData,
                reportId: reportId
            };
            localStorage.setItem('pendingDiagnosisData', JSON.stringify(dataToSave));
            
            // Show alert prompting to login/register
            const result = await Swal.fire({
                title: 'Sign In / Sign Up Required',
                text: 'Please sign in or create an account to save your diagnosis report and proceed to payment.',
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#069668',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Sign In / Sign Up',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                navigate('/login');
            }
            return;
        }

        const calculatedExpenses = calculateExpenses(currentData);

        // Calculate Business Profit & Tax if applicable
        let businessTaxDetails = {};
        if (currentData.personalInfo.incomeType === 'business' && currentData.personalInfo.businessCategory === 'manufacturing') {
            const turnover = parseFloat(currentData.businessDetails.grossTurnover) || 0;
            const otherIncome = parseFloat(currentData.businessDetails.otherIncomeAmount) || 0;
            const totalIncome = turnover + otherIncome;
            const netProfit = totalIncome - (calculatedExpenses.totalExpenses || 0);

            businessTaxDetails = calculateBusinessTax(netProfit);
        } else if (currentData.personalInfo.incomeType === 'business_partnership') {
            // Use calculations from PartnershipTaxForm
            const rev = parseFloat(currentData.businessDetails.totalAnnualRevenue) || 0;
            const exp = parseFloat(currentData.businessDetails.totalExpenses) || 0;
            const rem = parseFloat(currentData.businessDetails.remunerationToPartners) || 0;
            const int = parseFloat(currentData.businessDetails.interestOnCapital) || 0;

            const netProfit = rev - exp;
            const taxableInc = Math.max(0, netProfit - rem - int);

            businessTaxDetails = {
                tax: Math.round(taxableInc * 0.30),
                cess: Math.round(taxableInc * 0.30 * 0.04),
                totalTaxPayable: Math.round(taxableInc * 0.312),
                netProfitBeforeTax: netProfit,
                taxableIncome: taxableInc
            };
        }

        // Prepare report data
        const reportData = {
            reportId: reportId,
            userId: user?.id || user?.uid, // Tie the report to the logged-in user
            personalInfo: currentData.personalInfo,
            businessDetails: currentData.businessDetails,

            // Detailed data for report analysis
            businessExpenses: currentData.businessExpenses,
            businessInvestment: currentData.businessInvestment,
            salaryIncome: currentData.salaryIncome,
            salaryExpenses: currentData.salaryExpenses,
            salaryInvestment: currentData.salaryInvestment,
            jointSalary: currentData.jointSalary, // Added for couple

            salary: currentData.salary,
            expenses: calculatedExpenses,
            landGold: currentData.landGold,
            taxDetails: currentData.taxDetails,
            businessAnalysis: (currentData.personalInfo.incomeType === 'business' && currentData.personalInfo.businessCategory === 'manufacturing') || currentData.personalInfo.incomeType === 'business_partnership' ? {
                ...businessTaxDetails,
                netProfitBeforeTax: businessTaxDetails.netProfitBeforeTax || ((parseFloat(currentData.businessDetails.grossTurnover) || 0) + (parseFloat(currentData.businessDetails.otherIncomeAmount) || 0) - (calculatedExpenses.totalExpenses || 0))
            } : {},
            payment: {
                paymentId: isAdminDemo ? 'DEMO_' + Date.now() : 'DUMMY_' + Date.now(),
                amount: 299,
                currency: 'INR',
                status: 'success',
                paidAt: new Date()
            }
        };

        setIsProcessing(true);

        // If it's admin demo, skip Razorpay
        if (isAdminDemo) {
            try {
                await saveDiagnosisReport(reportData);
                
                // Record referral if a franchise referred this user
                if (currentData.personalInfo.referredBy) {
                    await recordReferral(
                        currentData.personalInfo.referredBy, 
                        currentData.personalInfo.name, 
                        currentData.personalInfo.email,
                        new Date().toLocaleDateString(),
                        'Completed'
                    );
                }

                localStorage.setItem('diagnosisReport', JSON.stringify(reportData));
                const redirectPath = isAdminDemo ? `/admin/diagnosis/reports/${reportData.reportId}` : `/diagnosis-report/${reportData.reportId}`;
                navigate(redirectPath);
                return;
            } catch (error) {
                console.error('Error saving demo report:', error);
                alert('Error generating demo report.');
                setIsProcessing(false);
                return;
            }
        }

        try {
            await initiatePayment({
                amount: 29900,
                description: 'Financial Diagnosis Report',
                prefill: {
                    name: currentData.personalInfo.name,
                    contact: currentData.personalInfo.phone
                },
                notes: {
                    diagnosis_type: 'financial_health'
                },
                onSuccess: async (response) => {
                    console.log('Payment successful:', response);
                    reportData.payment.paymentId = response.razorpay_payment_id;

                    try {
                        await saveDiagnosisReport(reportData);
                        
                        // Record referral if a franchise referred this user
                        if (currentData.personalInfo.referredBy) {
                            await recordReferral(
                                currentData.personalInfo.referredBy, 
                                currentData.personalInfo.name, 
                                currentData.personalInfo.email,
                                new Date().toLocaleDateString(),
                                'Completed'
                            );
                        }

                        localStorage.setItem('diagnosisReport', JSON.stringify(reportData));
                        navigate(`/diagnosis-report/${reportData.reportId}`);
                    } catch (firebaseError) {
                        console.error('Error saving to Firebase:', firebaseError);
                        alert('Report generated but there was an issue saving.');
                        navigate(`/diagnosis-report/${reportData.reportId}`);
                    }
                },
                onFailure: (error) => {
                    console.error('Payment failed:', error);
                    alert(`Payment failed: ${error.description || 'Please try again'}`);
                    setIsProcessing(false);
                },
                onDismiss: () => {
                    console.log('Payment cancelled by user');
                    setIsProcessing(false);
                }
            });
        } catch (error) {
            console.error('Error initiating payment:', error);
            alert('Error processing payment. Please try again.');
            setIsProcessing(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Step1PersonalInfo
                        data={formData.personalInfo}
                        updateData={(data) => updateFormData('personalInfo', data)}
                        nextStep={nextStep}
                        user={user}
                    />
                );
            case 2:
                // If couple, show JointFinancialForm
                if (formData.personalInfo.familyType === 'couple') {
                    return (
                        <JointFinancialForm
                            data={formData.jointSalary}
                            updateData={(data) => updateFormData('jointSalary', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            isProcessing={isProcessing}
                        />
                    );
                }

                // Show different forms based on income type
                if (formData.personalInfo.incomeType === 'business_partnership') {
                    return (
                        <PartnershipTaxForm
                            data={formData.businessDetails}
                            updateData={(data) => updateFormData('businessDetails', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            isLastStep={true} 
                            isProcessing={isProcessing}
                        />
                    );
                } else if (formData.personalInfo.incomeType === 'business') {
                    return (
                        <BusinessIncomeForm
                            data={formData.businessDetails}
                            updateData={(data) => updateFormData('businessDetails', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            incomeType={formData.personalInfo.incomeType}
                            businessCategory={formData.personalInfo.businessCategory} // Pass category
                        />
                    );
                } else {
                    // Salary users - Part 1: Fixed Expenses & Income
                    return (
                        <SalaryIncomeForm
                            data={formData.salaryIncome}
                            updateData={(data) => updateFormData('salaryIncome', data)}
                            personalInfo={formData.personalInfo}
                            updatePersonalInfo={(data) => updateFormData('personalInfo', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    );
                }
            case 3:
                // Show different expense forms based on income type
                if (formData.personalInfo.incomeType === 'business' || formData.personalInfo.incomeType === 'business_partnership') {
                    return (
                        <BusinessExpensesForm
                            data={formData.businessExpenses}
                            updateData={(data) => updateFormData('businessExpenses', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            businessCategory={formData.personalInfo.businessCategory}
                        />
                    );
                } else {
                    // Salary users - Part 2: Living Expenses
                    return (
                        <SalaryExpensesForm
                            data={formData.salaryExpenses}
                            updateData={(data) => updateFormData('salaryExpenses', data)}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    );
                }
            case 4:
                // Show BusinessInvestmentForm for business users, SalaryInvestmentForm for salary users
                if (formData.personalInfo.incomeType === 'business' || formData.personalInfo.incomeType === 'business_partnership') {
                    return (
                        <BusinessInvestmentForm
                            data={formData.businessInvestment}
                            updateData={(data) => updateFormData('businessInvestment', data)}
                            businessDetails={formData.businessDetails}
                            businessExpenses={formData.businessExpenses}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            isLastStep={true}
                            isProcessing={isProcessing}
                        />
                    );
                } else {
                    // Salary users - Part 3: Discretionary & Investments
                    // Not the last step anymore - goes to Summary next
                    return (
                        <SalaryInvestmentForm
                            data={formData.salaryInvestment}
                            updateData={(data) => updateFormData('salaryInvestment', data)}
                            personalInfo={formData.personalInfo}
                            salaryIncome={formData.salaryIncome}
                            salaryExpenses={formData.salaryExpenses}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            isLastStep={false}
                            isProcessing={isProcessing}
                        />
                    );
                }
            case 5:
                // Land & Gold Assets step for ALL users (Salary & Business)
                return (
                    <Step6LandGold
                        data={formData.landGold}
                        updateData={(data) => updateFormData('landGold', data)}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        isLastStep={false}
                        isProcessing={isProcessing}
                        formData={formData}
                    />
                );
            case 6:
                // Summary step for ALL users (FINAL STEP)
                return (
                    <Step5Summary
                        formData={formData}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        isLastStep={true}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="diagnosis-form-wrapper">
            <div className="diagnosis-form-container">
                {/* Progress Indicator */}
                <div className="progress-indicator">
                    <div className="progress-steps">
                        {(formData.personalInfo.familyType === 'couple'
                            ? [1, 2, 6] // Joint form is Step 2, then Summary Step 6
                            : formData.personalInfo.incomeType === 'business_partnership'
                                ? [1, 2, 5, 6]
                                : [1, 2, 3, 4, 5, 6]
                        ).map((step) => (
                            <div
                                key={step}
                                className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                            >
                                <div className="step-circle">
                                    {currentStep > step ? '✓' : step}
                                </div>
                                <div className="step-label">
                                    {step === 1 && 'Basic Info'}
                                    {step === 2 && (
                                        formData.personalInfo.familyType === 'couple' ? 'Joint Details' :
                                        formData.personalInfo.incomeType === 'business_partnership' ? 'Generate' : 'Income'
                                    )}
                                    {step === 3 && 'Expenses'}
                                    {step === 4 && (formData.personalInfo.incomeType === 'salary' ? 'Investments' : 'Generate')}
                                    {step === 5 && 'Land & Gold'}
                                    {step === 6 && 'Summary'}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-bar-fill"
                            style={{ 
                                width: `${
                                    currentStep === 1 ? (1/3 * 100) :
                                    currentStep === 2 && formData.personalInfo.familyType === 'couple' ? (2/3 * 100) :
                                    currentStep === 6 ? 100 :
                                    (currentStep / (formData.personalInfo.incomeType === 'salary' ? 6 : 4)) * 100
                                }%` 
                            }}
                        />
                    </div>
                </div>

                {/* Form Content */}
                <div className="form-content">
                    {renderStep()}
                </div>
            </div>
        </div>
    );
};

export default FinancialDiagnosisForm;
